// ============================================================================
// General coupons — any customer can apply them, any number of times, until
// they expire. Unlike client coupons (client-details page) they are not tied to
// one client and are not switched off when an order is placed.
//
// Gated by the `coupon.read/create/update/delete` permissions (admin by default,
// changed in Branch → Permissions). The backend enforces the same slugs. A coupon
// that any PI carries can't be deleted (`can_delete`) — only disabled.
// ============================================================================
import { Button, Empty, Form, message, Popconfirm, Space, Spin, Switch, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import {
  CouponTemplateDetail,
  createGeneralCoupon,
  fetchCouponTemplate,
  updateGeneralCoupon,
  deleteGeneralCoupon,
  fetchGeneralCoupons,
  GeneralCoupon,
  setGeneralCouponActive,
} from "../../../apis/client.api";
import CouponFormModal, {
  discountLabel,
  inr,
} from "../../misc/coupon_form_modal";

const STATUS_TAG: Record<GeneralCoupon["status"], { color: string; label: string }> = {
  active: { color: "green", label: "Active" },
  expired: { color: "default", label: "Expired" },
  inactive: { color: "default", label: "Inactive" },
};

const GeneralCoupons: React.FC = () => {
  const [rows, setRows] = useState<GeneralCoupon[]>([]);
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("coupon.create");
  const canUpdate = hasPermission("coupon.update");
  const canDelete = hasPermission("coupon.delete");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // Set when the modal edits an existing coupon rather than creating one.
  const [editing, setEditing] = useState<(CouponTemplateDetail & { times_used: number }) | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<number | null>(null);

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = async (c: GeneralCoupon) => {
    setLoadingEditId(c.coupon_id);
    const detail = await fetchCouponTemplate(c.coupon_id);
    setLoadingEditId(null);
    if (!detail) return;
    setEditing({ ...detail, times_used: c.times_used });
    setOpen(true);
  };

  const load = async () => {
    setLoading(true);
    const res = await fetchGeneralCoupons();
    setRows(res.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const [busyId, setBusyId] = useState<number | null>(null);
  const runAction = async (id: number, action: () => Promise<unknown>, done: string) => {
    setBusyId(id);
    try {
      await action();
      message.success(done);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const columns: ColumnsType<GeneralCoupon> = [
    {
      title: "Code",
      dataIndex: "coupon_code",
      render: (code: string, c) => (
        <>
          <b style={{ fontFamily: "monospace" }}>{code}</b>
          {c.is_hidden && <Tag style={{ marginLeft: 8 }}>Hidden</Tag>}
        </>
      ),
    },
    {
      title: "Discount",
      render: (_, c) => (
        <>
          {discountLabel(c)}
          {c.is_free_delivery && <Tag color="blue" style={{ marginLeft: 8 }}>Free delivery</Tag>}
        </>
      ),
    },
    {
      title: "Min order",
      dataIndex: "min_cart_value",
      render: (v: number) => (v > 0 ? inr.format(v) : "—"),
    },
    {
      title: "3-month purchases above",
      dataIndex: "cumulative_sum_above",
      render: (v: number) => (v > 0 ? inr.format(v) : "—"),
    },
    {
      title: "Valid till",
      dataIndex: "valid_till",
      render: (d: string) => dayjs(d).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: GeneralCoupon["status"]) => (
        <Tag color={STATUS_TAG[s]?.color}>{STATUS_TAG[s]?.label ?? s}</Tag>
      ),
    },
    { title: "Orders", dataIndex: "times_used" },
    { title: "Note", dataIndex: "description", render: (d: string | null) => d || "—" },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (d: string) => dayjs(d).format("DD MMM YYYY"),
    },
    ...(canUpdate || canDelete
      ? [
          {
            title: "",
            key: "actions",
            render: (_: unknown, c: GeneralCoupon) => (
              <Space size={0}>
                {canUpdate && (
                  <Button
                    type="link"
                    size="small"
                    loading={loadingEditId === c.coupon_id}
                    onClick={() => openEdit(c)}
                  >
                    Edit
                  </Button>
                )}
                {canUpdate && (
                  <Popconfirm
                    title={c.is_active ? `Disable ${c.coupon_code}?` : `Enable ${c.coupon_code}?`}
                    description={
                      c.is_active
                        ? "Customers can no longer apply it. PIs that already have it keep it."
                        : "Customers can apply it again until it expires."
                    }
                    okText={c.is_active ? "Disable" : "Enable"}
                    onConfirm={() =>
                      runAction(
                        c.coupon_id,
                        () => setGeneralCouponActive(c.coupon_id, !c.is_active),
                        c.is_active ? "Coupon disabled" : "Coupon enabled"
                      )
                    }
                  >
                    <Button type="link" size="small" loading={busyId === c.coupon_id}>
                      {c.is_active ? "Disable" : "Enable"}
                    </Button>
                  </Popconfirm>
                )}
                {canDelete && (c.can_delete ? (
                  <Popconfirm
                    title={`Delete ${c.coupon_code}?`}
                    description="This can't be undone."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() =>
                      runAction(c.coupon_id, () => deleteGeneralCoupon(c.coupon_id), "Coupon deleted")
                    }
                  >
                    <Button type="link" size="small" danger disabled={busyId === c.coupon_id}>
                      Delete
                    </Button>
                  </Popconfirm>
                ) : (
                  <Tooltip title="Already used on a PI/order — disable it instead">
                    <Button type="link" size="small" danger disabled>
                      Delete
                    </Button>
                  </Tooltip>
                ))}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className="pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="align-middle th-font-size-18 th-font-weight-600 primary-color-text">
          General coupons ({rows.length})
        </div>
        {canCreate && (
          <Button size="small" onClick={openCreate}>
            Create coupon
          </Button>
        )}
      </div>

      <CouponFormModal
        open={open}
        editing={editing}
        title={editing ? `Edit ${editing.coupon_code}` : "Create general coupon"}
        intro={
          editing
            ? editing.times_used > 0
              ? `Used on ${editing.times_used} order${editing.times_used === 1 ? "" : "s"}. PIs already made keep the discount they were made with; changes apply wherever the coupon is applied next.`
              : "Changes apply wherever the coupon is applied next."
            : "Any customer can use it, any number of times, until it expires."
        }
        descriptionPlaceholder="Shown to customers with the coupon"
        extraFields={
          <Form.Item
            label="Hide from coupon list"
            name="is_hidden"
            valuePropName="checked"
            extra="Hidden coupons still work when the code is typed in, but customers won't see them listed."
          >
            <Switch />
          </Form.Item>
        }
        describeExtra={(extra) => [
          { label: "Hidden from coupon list", value: extra.is_hidden ? "Yes" : "No" },
        ]}
        onClose={() => setOpen(false)}
        onSubmit={(body, extra) =>
          editing
            ? updateGeneralCoupon(editing.coupon_id, { ...body, is_hidden: !!extra.is_hidden })
            : createGeneralCoupon({ ...body, is_hidden: !!extra.is_hidden })
        }
        onCreated={load}
      />

      {loading ? (
        <Spin style={{ display: "block", padding: 24 }} />
      ) : rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No general coupons yet" />
      ) : (
        <Table
          rowKey="coupon_id"
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      )}
    </div>
  );
};

export default GeneralCoupons;
